import { useCallback, useEffect, useRef, useState } from "react";
import { errorText } from "../../i18n/serverErrors";
import {
  shopModeApi,
  type FloorAssignableCourier,
  type FloorPayoutPreview,
} from "../../api/shopMode";
import { useT } from "../../i18n/LanguageProvider";
import { money } from "../../i18n/intl";
import { OrderWhenFields } from "./OrderWhenFields";
import {
  emptyScheduleDraft,
  scheduleOwedEur,
  type ScheduleDraft,
} from "./scheduleDraft";

interface Props {
  value: ScheduleDraft;
  onChange(next: ScheduleDraft): void;
  /** The ticket's goods total — "Paid in full" adds the delivery fee to it. */
  goodsTotal: number;
  disabled?: boolean;
}

/**
 * G-112 (OD-94): "Schedule order" on the till — the ticket becomes an ORDER,
 * never a sale: pickup or delivery, a day from the calendar and a time,
 * recipient name + phone, and what was paid now (none / part / full; the rest
 * is taken before it leaves the shop, OD-101). Replaces WP-C's "Send with
 * courier", whose courier fields (fee, package, assign now with a live
 * payout preview) live on in the Delivery half.
 */
export function ShopFloorScheduleBlock({ value, onChange, goodsTotal, disabled }: Props) {
  const { t } = useT();
  const patch = useCallback(
    (partial: Partial<ScheduleDraft>) => onChange({ ...value, ...partial }),
    [value, onChange],
  );
  const isDelivery = value.enabled && value.fulfillment === "COURIER";

  const [couriers, setCouriers] = useState<FloorAssignableCourier[]>([]);
  const [couriersLoading, setCouriersLoading] = useState(false);
  const [couriersLoaded, setCouriersLoaded] = useState(false);

  useEffect(() => {
    if (!isDelivery || couriersLoaded || couriersLoading) return;
    setCouriersLoading(true);
    shopModeApi
      .floorListDispatchCouriers()
      .then(setCouriers)
      .catch(() => setCouriers([]))
      .finally(() => {
        setCouriersLoaded(true);
        setCouriersLoading(false);
      });
  }, [isDelivery, couriersLoaded, couriersLoading]);

  // Live payout preview when a courier + fee are set.
  const [preview, setPreview] = useState<FloorPayoutPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const previewSeq = useRef(0);

  useEffect(() => {
    const price = Number(value.priceEur);
    if (
      !isDelivery ||
      !value.courierUserId ||
      !value.priceEur.trim() ||
      !Number.isFinite(price)
    ) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    const distanceKm = value.distanceKm.trim() ? Number(value.distanceKm) : undefined;
    const seq = ++previewSeq.current;
    setPreviewLoading(true);
    setPreviewError(null);
    const timer = window.setTimeout(() => {
      shopModeApi
        .floorPreviewDispatchPayout({
          courierUserId: value.courierUserId,
          priceEur: price,
          distanceKm: Number.isFinite(distanceKm ?? NaN) ? distanceKm : undefined,
        })
        .then((res) => {
          if (seq === previewSeq.current) setPreview(res);
        })
        .catch((err: unknown) => {
          if (seq === previewSeq.current) {
            setPreview(null);
            setPreviewError(errorText(err, "courier.couldNotPreview"));
          }
        })
        .finally(() => {
          if (seq === previewSeq.current) setPreviewLoading(false);
        });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [isDelivery, value.courierUserId, value.priceEur, value.distanceKm]);

  if (!value.enabled) {
    return (
      <button
        type="button"
        className="button tinted"
        onClick={() => patch({ enabled: true })}
        disabled={disabled}
      >
        {t("sale.scheduleOrder")}
      </button>
    );
  }

  const owed = scheduleOwedEur(value, goodsTotal);

  return (
    <div className="shop-floor-sale-schedule">
      <div className="shop-floor-sale-schedule-head">
        <span>{t("sale.scheduleOrder")}</span>
        <button
          type="button"
          className="button plain"
          onClick={() => onChange(emptyScheduleDraft())}
          disabled={disabled}
        >
          {t("sale.sellNowInstead")}
        </button>
      </div>

      <div className="shop-floor-sale-schedule-form">
        <div className="button-row">
          {(["PICKUP", "COURIER"] as const).map((f) => (
            <button
              key={f}
              type="button"
              className={value.fulfillment === f ? "button" : "button tinted"}
              onClick={() => patch({ fulfillment: f })}
              disabled={disabled}
            >
              {f === "PICKUP" ? t("orderCreate.storePickup") : t("orderCreate.delivery")}
            </button>
          ))}
        </div>

        <label className="field">
          <span>{t("courier.recipient")}</span>
          <input
            value={value.recipientName}
            disabled={disabled}
            onChange={(e) => patch({ recipientName: e.target.value })}
          />
        </label>
        <label className="field">
          <span>{t("courier.recipientPhone")}</span>
          <input
            type="tel"
            value={value.recipientPhone}
            disabled={disabled}
            onChange={(e) => patch({ recipientPhone: e.target.value })}
          />
        </label>

        <OrderWhenFields
          idPrefix="sf-sched"
          value={value.when}
          onChange={(when) => patch({ when })}
          disabled={disabled}
        />

        {isDelivery ? (
          <>
            <label className="field">
              <span>{t("courier.address")}</span>
              <textarea
                rows={2}
                value={value.deliveryAddress}
                disabled={disabled}
                onChange={(e) => patch({ deliveryAddress: e.target.value })}
              />
            </label>
            <label className="field">
              <span>{t("courier.notes")}</span>
              <input
                value={value.recipientNotes}
                disabled={disabled}
                onChange={(e) => patch({ recipientNotes: e.target.value })}
              />
            </label>
            <div className="shop-floor-sale-schedule-row">
              <label className="field">
                <span>{t("courier.fee")}</span>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  inputMode="decimal"
                  value={value.priceEur}
                  disabled={disabled}
                  onChange={(e) => patch({ priceEur: e.target.value })}
                />
              </label>
              <label className="field">
                <span>{t("courier.package")}</span>
                <select
                  value={value.packageSize}
                  disabled={disabled}
                  onChange={(e) =>
                    patch({ packageSize: e.target.value as ScheduleDraft["packageSize"] })
                  }
                >
                  <option value="">{t("courier.packageNotSet")}</option>
                  <option value="SMALL">{t("courier.packageSmall")}</option>
                  <option value="MEDIUM">{t("courier.packageMedium")}</option>
                  <option value="LARGE">{t("courier.packageLarge")}</option>
                </select>
              </label>
            </div>

            <label className="field">
              <span>{t("courier.assignNow")}</span>
              <select
                value={value.courierUserId}
                disabled={disabled || couriersLoading}
                onChange={(e) => patch({ courierUserId: e.target.value })}
              >
                <option value="">
                  {couriersLoading ? t("courier.loadingCouriers") : t("courier.leaveForBoard")}
                </option>
                {couriers.map((c) => (
                  <option key={c.id} value={c.id} disabled={!c.isAvailable}>
                    {c.fullName}
                    {c.vehicleType ? ` · ${c.vehicleType.toLowerCase()}` : ""}
                    {c.isAvailable ? "" : ` ${t("courier.unavailable")}`}
                  </option>
                ))}
              </select>
            </label>

            {value.courierUserId ? (
              <div className="shop-floor-sale-schedule-payout" aria-live="polite">
                {previewLoading ? (
                  <span className="muted">{t("courier.calculating")}</span>
                ) : previewError ? (
                  <span className="alert error">{previewError}</span>
                ) : preview?.payoutEur ? (
                  <span>
                    {t("courier.payoutLabel")} <strong>{money(preview.payoutEur)}</strong> ·{" "}
                    {t("courier.marginLabel")} <strong>{money(preview.marginEur)}</strong>
                  </span>
                ) : preview?.requiresDistance ? (
                  <label className="field">
                    <span>{t("courier.distance")}</span>
                    <input
                      type="number"
                      min={0}
                      step="0.1"
                      value={value.distanceKm}
                      disabled={disabled}
                      onChange={(e) => patch({ distanceKm: e.target.value })}
                    />
                  </label>
                ) : preview && !preview.hasRule ? (
                  <label className="field">
                    <span>{t("courier.overridePayout")}</span>
                    <input
                      type="number"
                      min={0}
                      step="0.01"
                      value={value.overridePayoutEur}
                      disabled={disabled}
                      onChange={(e) => patch({ overridePayoutEur: e.target.value })}
                    />
                  </label>
                ) : null}
              </div>
            ) : (
              <p className="muted shop-floor-sale-schedule-hint">
                {t("courier.notAssignedHint")}
              </p>
            )}
          </>
        ) : null}

        <div className="field">
          <span>{t("schedule.paymentNow")}</span>
          <div className="button-row">
            {(
              [
                ["NONE", "schedule.payNone"],
                ["PART", "schedule.payPart"],
                ["FULL", "schedule.payFull"],
              ] as const
            ).map(([payment, label]) => (
              <button
                key={payment}
                type="button"
                className={value.payment === payment ? "button" : "button tinted"}
                onClick={() => patch({ payment })}
                disabled={disabled}
              >
                {t(label)}
              </button>
            ))}
          </div>
        </div>
        {value.payment === "PART" ? (
          <label className="field">
            <span>{t("schedule.partAmount")}</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              inputMode="decimal"
              value={value.partAmountEur}
              disabled={disabled}
              onChange={(e) => patch({ partAmountEur: e.target.value })}
            />
          </label>
        ) : null}
        {value.payment !== "NONE" ? (
          <div className="button-row">
            {(["CASH", "CARD"] as const).map((method) => (
              <button
                key={method}
                type="button"
                className={value.method === method ? "button" : "button tinted"}
                onClick={() => patch({ method })}
                disabled={disabled}
              >
                {method === "CASH" ? t("sale.cash") : t("sale.card")}
              </button>
            ))}
          </div>
        ) : null}
        <p className="muted shop-floor-sale-schedule-hint">
          {t("schedule.customerPays", { amount: money(owed) })}
          {value.payment !== "FULL" ? ` ${t("schedule.restBeforeLeaving")}` : ""}
        </p>
      </div>
    </div>
  );
}
